import { KindeProvider, useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { config } from "@noted/configuration";
import { ComponentProps, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { restPoster } from "~/api/fetchers";
import AuthProvider, { UseAuthType } from "~/components/auth-provider/index";
import { clearContextSession, getContextSession } from "~/components/auth-provider/shared";
import { isDevelopment } from "~/utilities/is-development";

export function KindeAuthProvider({ children }: ComponentProps<typeof AuthProvider>) {
  const navigate = useNavigate();
  return (
    <KindeProvider
      audience={config.auth.audience}
      clientId={config.auth.clientId}
      domain={config.auth.domain}
      redirectUri={config.appUrl}
      logoutUri={config.landingUrl}
      useInsecureForRefreshToken={isDevelopment()}
      refreshOnFocus={true}
      callbacks={{
        onSuccess: (_user, state) => {
          if (state?.redirectTo) {
            navigate(state.redirectTo);
          }
        },
      }}
    >
      {children}
    </KindeProvider>
  );
}

export function useKindeAuthHook(): Omit<UseAuthType, "session"> {
  const {
    isAuthenticated,
    isLoading,
    getToken: getKindeToken,
    login,
    logout: kindeLogout,
  } = useKindeAuth();

  const getToken = useCallback(() => getKindeToken().then(t => t ?? ""), [getKindeToken]);

  const logout = useCallback(async () => {
    try {
      if (getContextSession()) {
        await restPoster("/v1/auth/logout");
      }
    } finally {
      clearContextSession();
      await kindeLogout({ allSessions: true });
    }
  }, [restPoster, clearContextSession, kindeLogout, config.landingUrl]);

  return {
    isAuthenticated,
    isLoading,
    getToken,
    login,
    logout,
  };
}
